/**
 * rb-pdf.js — ATS-safe PDF export.
 *
 * A one-click, deterministic single-column vector PDF built with jsPDF from
 * the structured ResumeModel. Real selectable text (the fix for the old
 * rasterised-image bug), embedded Unicode font via the shared
 * window.__pdfLoadUnicodeFont helper so Cyrillic / Arabic / CJK render. Plain
 * layout — no sidebar, no photo, no graphics — for maximum ATS parsing.
 *
 * The styled, pixel-perfect export is rb-print.js; this is the parser-safe one.
 *
 * Depends on: RbI18n, RbExportText (download + loadScript). Lazy-loads jsPDF
 * and pdf-fonts.js.
 * globalThis.RbPdf
 */
(function () {
    'use strict';

    var T = function (k) { return globalThis.RbI18n.t(k); };

    function loadDeps() {
        return globalThis.RbExportText.loadVendor('jspdf').then(function () {
            return globalThis.RbExportText.loadScript('/js/pdf-fonts.js');
        });
    }

    /* mm geometry — A4 is 210×297, US Letter 215.9×279.4 */
    var MARGIN = 18, TOP = 16, BOTTOM = 16;
    var PT2MM = 0.352778;
    var PAGE_MM = { a4: { w: 210, h: 297 }, letter: { w: 215.9, h: 279.4 } };

    /* Pick the Noto script the résumé NEEDS from its actual text — not the
       page locale — so an Arabic or Korean résumé embeds the right font even
       when the UI is in another language. CJK/Arabic Noto fonts also cover
       Latin, so the "most complex script present" wins. */
    function detectScript(model) {
        var text = globalThis.RbState ? globalThis.RbState.allText(model) : '';
        if (/[؀-ۿ]/.test(text)) return 'arabic';
        if (/[가-힯]/.test(text)) return 'korean';
        if (/[぀-ヿ]/.test(text)) return 'japanese';
        if (/[一-鿿]/.test(text)) return 'chinese';
        if (/[Ѐ-ӿ]/.test(text)) return 'cyrillic';
        return 'latin';
    }

    function buildDoc(model, opts) {
        var D = function (k) { return globalThis.RbI18n.docT(k, model); };
        var fmt = (opts && opts.dateFormat) || 'MM/YYYY';
        var F = function (v) { return globalThis.RbI18n.formatDate(v, fmt); };
        var LV = function (v) { return v === 'native' ? D('prof_native') : String(v || '').toUpperCase(); };
        var jsPDF = globalThis.jspdf.jsPDF;
        var pgId = (model.meta && model.meta.pageSize === 'letter') ? 'letter' : 'a4';
        var PAGE_W = PAGE_MM[pgId].w, PAGE_H = PAGE_MM[pgId].h;
        var CONTENT_W = PAGE_W - MARGIN * 2;
        var doc = new jsPDF({ unit: 'mm', format: pgId, compress: true });
        var script = detectScript(model);
        return Promise.resolve(
            globalThis.__pdfLoadUnicodeFont ? globalThis.__pdfLoadUnicodeFont(doc, script) : 'helvetica'
        ).then(function (family) {
            family = family || 'helvetica';
            var weights = doc._unicodeFontWeights || ['normal'];
            var hasBold = weights.indexOf('bold') > -1;
            var rtl = !!opts.rtl;
            var y = TOP;

            function setFont(sizePt, bold) {
                doc.setFont(family, bold && hasBold ? 'bold' : 'normal');
                doc.setFontSize(sizePt);
            }
            function lineH(sizePt) { return sizePt * PT2MM * 1.2; }
            function ensure(space) {
                if (y + space > PAGE_H - BOTTOM) { doc.addPage(); y = TOP; }
            }
            function text(str, sizePt, bold, color, indent) {
                if (str == null || str === '') return;
                setFont(sizePt, bold);
                doc.setTextColor(color || '#1a1a2e');
                var x = MARGIN + (indent || 0);
                var w = CONTENT_W - (indent || 0);
                var lines = doc.splitTextToSize(String(str), w);
                var lh = lineH(sizePt);
                for (var i = 0; i < lines.length; i++) {
                    ensure(lh);
                    doc.text(lines[i], rtl ? (PAGE_W - MARGIN) : x, y, rtl ? { align: 'right' } : undefined);
                    y += lh;
                }
            }
            function gap(mm) { y += mm; }
            function rule(color) {
                ensure(2);
                doc.setDrawColor(color || '#c8ccd4');
                doc.setLineWidth(0.3);
                doc.line(MARGIN, y, PAGE_W - MARGIN, y);
                y += 2.4;
            }
            function heading(str) {
                gap(3.2);
                ensure(lineH(11) + 3);
                text(String(str).toUpperCase(), 10.5, true, '#2a2a3a');
                y -= 0.6;
                rule();
            }
            function bullet(str, sizePt) {
                setFont(sizePt, false);
                var w = CONTENT_W - 4;
                var lines = doc.splitTextToSize(String(str), w);
                var lh = lineH(sizePt);
                for (var i = 0; i < lines.length; i++) {
                    ensure(lh);
                    var bx = rtl ? (PAGE_W - MARGIN) : MARGIN;
                    if (i === 0) {
                        doc.text('•', bx, y, rtl ? { align: 'right' } : undefined);
                    }
                    doc.text(lines[i], rtl ? (PAGE_W - MARGIN - 4) : (MARGIN + 4), y, rtl ? { align: 'right' } : undefined);
                    y += lh;
                }
            }

            var p = model.personal || {};
            /* header */
            text(p.name || '', 19, true, '#111827');
            if (p.title) { gap(0.5); text(p.title, 11.5, false, '#444'); }
            var contact = ['email', 'phone', 'city', 'address', 'website', 'linkedin', 'github']
                .map(function (k) { return p[k]; }).filter(Boolean);
            if (contact.length) { gap(1); text(contact.join('   |   '), 9, false, '#555'); }
            var cult = model.cultural || {};
            var det = [];
            if (cult.dob && p.dob) det.push(D('date_of_birth') + ': ' + String(p.dob).split('-').reverse().join('.'));
            if (cult.marital && p.marital) det.push(D('marital_' + p.marital));
            if (cult.nationality && p.nationality) det.push(p.nationality);
            if (cult.religion && p.religion) det.push(p.religion);
            if (cult.gender && p.gender) det.push(D('gender_' + p.gender));
            if (det.length) { gap(0.5); text(det.join('  ·  '), 9, false, '#555'); }
            var prefs = globalThis.RbRender && globalThis.RbRender.prefPairs ? globalThis.RbRender.prefPairs(model) : [];
            if (prefs.length) { gap(0.5); text(prefs.map(function (pp) { return pp[0] + ': ' + pp[1]; }).join('   ·   '), 9, false, '#444'); }
            gap(1.5);

            function dateStr(a, b, cur) {
                return [F(a), cur ? D('present') : F(b)].filter(Boolean).join(' – ');
            }

            (model.meta.sectionOrder || []).forEach(function (sec) {
                if (sec === 'personal') return;
                if (sec === 'summary') {
                    if (model.summary) { heading(D('section_summary')); text(model.summary, 10, false, '#1a1a2e'); }
                    return;
                }
                var items = model[sec] || [];
                if (!items.length) return;
                heading(D('section_' + sec));

                if (sec === 'experience') {
                    items.forEach(function (x) {
                        gap(1);
                        text(x.position || '', 10.5, true, '#1a1a2e');
                        var sub = [x.company, x.location].filter(Boolean).join(', ');
                        var dr = dateStr(x.startDate, x.endDate, x.current);
                        if (sub || dr) text([sub, dr].filter(Boolean).join('   ·   '), 9, false, '#666');
                        if (x.description) text(x.description, 10, false, '#1a1a2e');
                        String(x.achievements || '').split('\n').filter(function (a) { return a.trim(); })
                            .forEach(function (a) { bullet(a.trim(), 10); });
                    });
                } else if (sec === 'education') {
                    items.forEach(function (x) {
                        gap(1);
                        text([x.degree, x.field].filter(Boolean).join(', ') || x.institution || '', 10.5, true, '#1a1a2e');
                        var line = [x.institution, dateStr(x.startDate, x.endDate, false),
                                    x.gpa ? D('gpa') + ': ' + x.gpa : '', x.honors].filter(Boolean).join('   ·   ');
                        if (line) text(line, 9, false, '#666');
                    });
                } else if (sec === 'skills') {
                    text(items.map(function (s) { return s.name; }).filter(Boolean).join('  ·  '), 10, false, '#1a1a2e');
                } else if (sec === 'languages') {
                    text(items.map(function (l) { return l.name + (l.proficiency ? ' (' + LV(l.proficiency) + ')' : ''); }).join('  ·  '), 10, false, '#1a1a2e');
                } else {
                    items.forEach(function (x) {
                        var vals = Object.keys(x).filter(function (k) { return k !== 'id' && x[k]; })
                            .map(function (k) { return x[k]; });
                        text(vals.join('  —  '), 10, false, '#1a1a2e');
                    });
                }
            });

            (model.custom || []).forEach(function (cs) {
                if (!cs.title && !(cs.items || []).some(Boolean)) return;
                heading(cs.title || D('section_custom'));
                (cs.items || []).filter(Boolean).forEach(function (i) { text(i, 10, false, '#1a1a2e'); });
            });

            return doc;
        });
    }

    function exportPdf(model, opts) {
        opts = opts || {};
        return loadDeps().then(function () {
            return buildDoc(model, opts);
        }).then(function (doc) {
            return globalThis.RbExportText.download(doc.output('blob'), globalThis.RbExportText.safeName(model, 'pdf'));
        });
    }

    /* For tests: return the PDF as a Blob without downloading. */
    function toBlob(model, opts) {
        return loadDeps().then(function () {
            return buildDoc(model, opts || {});
        }).then(function (doc) {
            return doc.output('blob');
        });
    }

    globalThis.RbPdf = { exportPdf: exportPdf, toBlob: toBlob };
})();
